import { Box, Typography, Button, Tabs, Tab } from "@mui/material";
import { prescriptionApi } from "apis";
import { CustomBreadcrumbs } from "components/common/CustomBreadcrumbs";
import DashboardLayout from "components/layouts/DashboardLayout";
import { EPath } from "constants/path";
import { useToast } from "contexts/ToastContext";
import {
  CreatePrescriptionOrderRequestDto,
  FormulaInPrescriptionDto,
  MedicineResponseDto,
  PrescriptionPatientDto,
} from "libs/cellera-sdk";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useCelleraTheme } from "theme/useCelleraTheme";

import Formula from "./components/Prescription/Formula/Formula";
import Medicine from "./components/Prescription/Medicine/Medicine";
import PatientSelectForPrescription from "./components/Prescription/PatientSelectForPrescription";
import { PrescriptionCreateSummary } from "./components/Prescription/PrescriptionCreateSummary";
import { PrescriptionPatientDetailsCard } from "./components/Prescription/PrescriptionPatientDetailsCard";
import { ShippingInfoForm } from "./components/Prescription/ShippingInfoForm";

export const PrescriptionCreatePage: React.FC = () => {
  const { colors } = useCelleraTheme();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [selectedPatient, setSelectedPatient] =
    useState<PrescriptionPatientDto | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formulas, setFormulas] = useState<FormulaInPrescriptionDto[]>([]);
  const [medicines, setMedicines] = useState<MedicineResponseDto[]>([]);
  const [prescriptionId, setPrescriptionId] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<"supplement" | "medicine">(
    "medicine"
  );
  const [shippingInfo, setShippingInfo] =
    useState<CreatePrescriptionOrderRequestDto>({
      shippingAddress: "",
      city: "",
      state: "",
      zip: "",
      country: "United States",
    });

  const selectedFormulaIds = useMemo(() => {
    return formulas.map((formula) => formula.formulaId);
  }, [formulas]);

  const selectedMedicineIds = useMemo(() => {
    return medicines.map((medicine) => medicine.id);
  }, [medicines]);

  // Calculate total price of selected formulas
  const formulaTotal = useMemo(() => {
    return formulas.reduce((sum, formula) => sum + formula.price, 0);
  }, [formulas]);

  const breadcrumbs = useMemo(
    () => [
      { label: "Dashboard", href: "/dashboard", order: 0 },
      {
        label: "Prescription Management",
        href: EPath.PRESCRIPTION_MANAGEMENT,
        order: 1,
      },
      {
        label: "Add New Prescription",
        href: EPath.PRESCRIPTION_CREATE,
        order: 2,
      },
    ],
    []
  );

  const handlePatientChange = useCallback(
    (patient: PrescriptionPatientDto | null) => {
      setSelectedPatient(patient);
    },
    []
  );

  const handlePatientRefreshed = useCallback(async () => {
    const { items } = await prescriptionApi.providerGetPatients({
      search: selectedPatient?.email || undefined,
      page: 1,
      pageSize: 50,
    });
    const refreshedPatient = items?.find(
      (item) => item.patientId === selectedPatient?.patientId
    );
    setSelectedPatient(refreshedPatient || null);
  }, [selectedPatient?.email, selectedPatient?.patientId]);

  const handleCancel = useCallback(() => {
    navigate(EPath.PRESCRIPTION_MANAGEMENT);
  }, [navigate]);

  const handleCreatePrescription = useCallback(async () => {
    if (!selectedPatient || prescriptionId) return prescriptionId ?? undefined;

    try {
      const { prescriptionId } =
        await prescriptionApi.providerCreatePrescription({
          createPrescriptionRequestDto: {
            patientId: selectedPatient.patientId,
          },
        });
      setPrescriptionId(prescriptionId);
      return prescriptionId;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to create prescription. Please try again.";
      showError("Create Prescription Failed", errorMessage);
      throw error;
    }
  }, [selectedPatient, prescriptionId, showError]);

  const isSaveDisabled = useMemo(
    () =>
      !prescriptionId ||
      (selectedFormulaIds.length === 0 && selectedMedicineIds.length === 0) ||
      !shippingInfo.shippingAddress ||
      !shippingInfo.city ||
      !shippingInfo.state ||
      !shippingInfo.zip ||
      !shippingInfo.country ||
      isSaving,
    [
      prescriptionId,
      selectedFormulaIds.length,
      selectedMedicineIds.length,
      shippingInfo.shippingAddress,
      shippingInfo.city,
      shippingInfo.state,
      shippingInfo.zip,
      shippingInfo.country,
      isSaving,
    ]
  );

  const isSaveEnabled = useMemo(
    () =>
      !!prescriptionId &&
      (selectedFormulaIds.length > 0 || selectedMedicineIds.length > 0) &&
      !!shippingInfo.shippingAddress &&
      !!shippingInfo.city &&
      !!shippingInfo.state &&
      !!shippingInfo.zip &&
      !!shippingInfo.country &&
      !isSaving,
    [
      prescriptionId,
      selectedFormulaIds.length,
      selectedMedicineIds.length,
      shippingInfo.shippingAddress,
      shippingInfo.city,
      shippingInfo.state,
      shippingInfo.zip,
      shippingInfo.country,
      isSaving,
    ]
  );

  const handleSave = useCallback(async () => {
    if (
      !selectedPatient ||
      !prescriptionId ||
      (selectedFormulaIds.length === 0 && selectedMedicineIds.length === 0)
    ) {
      showError(
        "Validation Error",
        "Please ensure patient is selected, prescription is created, and at least one item (formula or medicine) is added."
      );
      return;
    }

    // Validate shipping info
    if (
      !shippingInfo.shippingAddress ||
      !shippingInfo.city ||
      !shippingInfo.state ||
      !shippingInfo.zip ||
      !shippingInfo.country
    ) {
      showError(
        "Validation Error",
        "Please fill in all shipping information fields."
      );
      return;
    }

    setIsSaving(true);
    try {
      await prescriptionApi.providerCreatePrescriptionOrder({
        prescriptionId: prescriptionId,
        createPrescriptionOrderRequestDto: shippingInfo,
      });

      showSuccess(
        "Order Created",
        "The prescription order has been created successfully."
      );

      navigate(EPath.PRESCRIPTION_MANAGEMENT);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to create order. Please try again.";
      showError("Create Order Failed", errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [
    selectedPatient,
    prescriptionId,
    selectedFormulaIds,
    selectedMedicineIds,
    shippingInfo,
    navigate,
    showSuccess,
    showError,
  ]);

  return (
    <DashboardLayout title="Add New Prescription">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <CustomBreadcrumbs
          items={breadcrumbs}
          lastItem="Add New Prescription"
        />
      </Box>
      <Box sx={{ mb: 3, width: "100%" }}>
        <PatientSelectForPrescription
          onChange={handlePatientChange}
          label="Select Patient"
          placeholder="Select Patient"
        />
        <Typography
          variant="caption"
          sx={{
            color: colors.text.secondary,
            mt: 1,
            display: "block",
          }}
        >
          Please check the patient information carefully before prescribing.
        </Typography>
      </Box>

      {!!selectedPatient && (
        <>
          <PrescriptionPatientDetailsCard
            patient={selectedPatient}
            onRefreshed={handlePatientRefreshed}
          />

          {/* Tab Selector for Medicine/Supplement */}
          <Box sx={{ mb: 3, width: "100%" }}>
            <Tabs
              value={selectedTab}
              onChange={(_, newValue) => setSelectedTab(newValue)}
              sx={{
                borderBottom: 1,
                borderColor: colors.border.main,
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "1rem",
                  minWidth: 120,
                },
                "& .Mui-selected": {
                  color: colors.secondary.main,
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: colors.secondary.main,
                },
              }}
            >
              <Tab label="Medicine" value="medicine" />
              <Tab label="Supplement" value="supplement" />
            </Tabs>
          </Box>

          {/* Medicine Section */}
          {selectedTab === "medicine" && (
            <Box sx={{ mb: 3, width: "100%" }}>
              <Medicine
                prescriptionId={prescriptionId ?? undefined}
                onMedicinesChange={setMedicines}
                onCreatePrescription={handleCreatePrescription}
                isSaving={isSaving}
              />
            </Box>
          )}

          {/* Supplement Section */}
          {selectedTab === "supplement" && (
            <Box sx={{ mb: 3, width: "100%" }}>
              <Formula
                prescriptionId={prescriptionId ?? undefined}
                patientId={selectedPatient.patientId}
                onFormulasChange={setFormulas}
                onCreatePrescription={handleCreatePrescription}
                isSaving={isSaving}
              />
            </Box>
          )}

          {/* Fees & Total Summary */}
          <Box sx={{ mb: 3, width: "100%" }}>
            <PrescriptionCreateSummary formulaTotal={formulaTotal} />
          </Box>

          {/* Shipping Information */}
          <Box sx={{ mb: 3, width: "100%" }}>
            <ShippingInfoForm
              shippingInfo={shippingInfo}
              onChange={setShippingInfo}
            />
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              width: "100%",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{
                textTransform: "none",
                borderColor: colors.border.main,
                color: colors.text.primary,
                fontWeight: 600,
                borderRadius: "100px",
                px: 4,
                "&:hover": {
                  borderColor: colors.border.main,
                  backgroundColor: colors.background.secondary,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaveDisabled}
              sx={{
                textTransform: "none",
                backgroundColor: isSaveEnabled
                  ? colors.primary.main
                  : colors.background.secondary,
                color: isSaveEnabled
                  ? colors.text.inverse
                  : colors.text.secondary,
                fontWeight: 600,
                borderRadius: "100px",
                px: 4,
                "&:hover": {
                  backgroundColor: isSaveEnabled
                    ? colors.primary.dark
                    : undefined,
                },
                "&:disabled": {
                  backgroundColor: colors.background.secondary,
                  color: colors.text.secondary,
                },
              }}
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </Box>
        </>
      )}
    </DashboardLayout>
  );
};
